import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Handshake, ExternalLink, Sparkles, Users, Globe, ArrowRight } from "lucide-react";

const partners = [
  {
    name: "GrimWalker",
    // Category/description/audience/collaboration are user-generated / partner-facing content.
    // We keep them in the author's original language (Italian) — see phase 2 spec on
    // user-generated content. UI chrome and layout are translated.
    category: "Dark Fantasy / Web Game",
    logo: "https://assets.mytharion.app/grimwalker%20logo.png",
    website: "https://www.grim-walker.com",
    description:
      "È un'esperienza minimalista e atmosferica dove cammini in un mondo cupo, raccogli oggetti e affronti percorsi che prima o poi ti porteranno da qualche parte. L'interfaccia e il tono sono molto immersivi e nebbiosi.",
    audience:
      "Il progetto si rivolge principalmente agli appassionati di dark fantasy e grimdark, a chi ama atmosfere cupe, mondi desolati, toni malinconici e storie dove le cose finiscono male. È pensato anche per giocatori che apprezzano esperienze minimaliste e atmosferiche, con meccaniche semplici ma evocative, oltre che per un pubblico adulto e giovane adulto.",
    collaboration:
      "Una collaborazione tra GrimWalker e Mytharion ha un potenziale molto interessante, perché i due progetti sono complementari e parlano a pubblici affini: appassionati di dark fantasy, fantasy cupo, giochi di ruolo e mondi immersivi.",
  },
];

const AffiliatiPage = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen py-20 px-6">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
              <Handshake className="w-8 h-8 text-gold" />
            </div>
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-gold mb-5" data-testid="affiliati-title">
            {t("affiliati.title")}
          </h1>
          <p className="font-serif-fantasy italic text-lg md:text-xl text-parchment/90 max-w-3xl mx-auto">
            {t("affiliati.quote")}
          </p>
          <p className="mt-6 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t("affiliati.intro")}
          </p>
        </div>

        <section className="mb-12">
          <div className="rune-border bg-card/50 backdrop-blur rounded-xl p-6 md:p-8 transition-all duration-500 hover:drop-shadow-[0_0_20px_rgba(147,51,234,0.8)]">
            <div className="flex items-center gap-3 mb-5">
              <Sparkles className="w-6 h-6 text-gold shrink-0" />
              <h2 className="font-heading text-2xl text-foreground">
                {t("affiliati.sectionNetTitle")}
              </h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">{t("affiliati.netP1")}</p>
            <p className="mt-4 text-muted-foreground leading-relaxed">{t("affiliati.netP2")}</p>
            <p className="mt-4 text-muted-foreground leading-relaxed">{t("affiliati.netP3")}</p>
          </div>
        </section>

        <section>
          <div className="flex items-center gap-3 mb-8">
            <Handshake className="w-6 h-6 text-gold shrink-0" />
            <h2 className="font-heading text-2xl text-foreground">
              {t("affiliati.partnersTitle")}
            </h2>
          </div>

          <div className="space-y-10">
            {partners.map((partner) => (
              <article
                key={partner.name}
                className="w-full min-w-0 overflow-hidden rune-border bg-card/50 backdrop-blur rounded-xl p-6 md:p-8 transition-all duration-500 ease-out hover:drop-shadow-[0_0_25px_rgba(147,51,234,0.8)] hover:scale-[1.01]"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div className="flex items-center gap-5 min-w-0">
                    <div className="w-24 h-24 md:w-28 md:h-28 shrink-0 rounded-2xl bg-black/20 border border-gold/30 flex items-center justify-center overflow-hidden shadow-[0_0_20px_rgba(255,215,100,0.08)]">
                      <img
                        src={partner.logo}
                        alt={`${partner.name} logo`}
                        className="w-full h-full object-contain p-3"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-display text-3xl md:text-4xl text-gold break-words">
                        {partner.name}
                      </h3>
                      <p className="mt-2 text-sm text-muted-foreground">{partner.category}</p>
                    </div>
                  </div>

                  <a
                    href={partner.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 inline-flex items-center justify-center gap-2 rounded-md border border-gold/30 bg-gold/10 px-5 py-3 text-sm font-medium text-gold transition-all duration-300 hover:bg-gold/20 hover:border-gold/50 hover:scale-[1.02]"
                    data-testid={`partner-visit-${partner.name}`}
                  >
                    <Globe className="w-4 h-4" />
                    {t("affiliati.visitSite")}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>

                <div className="my-8 border-t border-border" />

                <div>
                  <h4 className="font-heading text-xl text-foreground mb-3">
                    {t("affiliati.project")}
                  </h4>
                  <p className="text-muted-foreground leading-relaxed">{partner.description}</p>
                </div>

                <div className="mt-8">
                  <div className="flex items-center gap-3 mb-3">
                    <Users className="w-5 h-5 text-gold shrink-0" />
                    <h4 className="font-heading text-xl text-foreground">
                      {t("affiliati.audience")}
                    </h4>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">{partner.audience}</p>
                </div>

                <div className="mt-8 rounded-xl bg-gold/5 border border-gold/20 p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Sparkles className="w-5 h-5 text-gold shrink-0" />
                    <h4 className="font-heading text-xl text-gold">
                      {t("affiliati.collaboration")}
                    </h4>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">{partner.collaboration}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <div className="rune-border bg-card/50 backdrop-blur rounded-xl p-6 md:p-8 text-center transition-all duration-500 hover:drop-shadow-[0_0_20px_rgba(255,215,100,0.6)]">
            <div className="flex justify-center mb-5">
              <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                <Handshake className="w-8 h-8 text-gold" />
              </div>
            </div>
            <h2 className="font-heading text-2xl text-foreground">{t("affiliati.ctaTitle")}</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              {t("affiliati.ctaDesc")}
            </p>
            <Link
              to="/collab"
              className="inline-flex items-center justify-center gap-2 mt-6 px-6 py-3 rounded-md bg-gold text-white font-semibold transition-all duration-300 hover:bg-gold/90 hover:scale-[1.02]"
              data-testid="affiliati-cta-propose"
            >
              {t("affiliati.ctaBtn")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        <div className="mt-12 pt-8 border-t border-border">
          <Link to="/" className="text-gold hover:text-gold/80 text-sm transition-colors">
            {t("affiliati.backHome")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AffiliatiPage;
